// Seite „Läufe“ (/workflows/laeufe): alle Läufe, filterbar nach Workflow, Lead und Status. Aktualisiert sich im Takt.
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { workflowsApi, type LaufStatus, type WorkflowLauf } from "../api/workflows";
import { useAuth } from "../auth/AuthContext";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { Auswahl } from "../components/Auswahl";
import { useSeitenKopf } from "../daten/seite";
import { useLaden } from "./daten";
import { LAUF_STATUS } from "./hilfen";
import { LaeufeTabelle, type LaufAktion } from "./LaeufeTabelle";

const STATUS: LaufStatus[] = ["wartet", "laeuft", "pausiert", "fertig", "fehler", "abgebrochen"];

export default function LaeufeSeite() {
  useSeitenKopf([{ label: "Workflows", to: "/workflows" }, { label: "Läufe" }]);
  const [params, setParams] = useSearchParams();
  const meldung = useMeldung();
  const { nutzer } = useAuth();
  const kannVerwalten = !!nutzer?.permissions.includes("workflows_verwalten");
  const workflowId = params.get("workflow_id") ?? undefined;
  const leadId = params.get("lead_id") ?? undefined;
  const status = (STATUS.includes(params.get("status") as LaufStatus) ? params.get("status") : undefined) as LaufStatus | undefined;
  const [mehr, setMehr] = useState<WorkflowLauf[]>([]);
  const [mehrCursor, setMehrCursor] = useState<string | null | undefined>(undefined);
  const [bearbeitet, setBearbeitet] = useState(false);

  const workflows = useLaden(() => workflowsApi.liste({ limit: 200 }), []);
  const archivierte = useLaden(() => workflowsApi.liste({ status: "archiviert", limit: 200 }), []);
  const g = useLaden(() => workflowsApi.laeufe({ workflow_id: workflowId, lead_id: leadId, status, limit: 50 }), [workflowId, leadId, status], 8000);

  const setFilter = (teil: Record<string, string | null>) => {
    const n = new URLSearchParams(params);
    for (const [k, v] of Object.entries(teil)) {
      if (v) n.set(k, v);
      else n.delete(k);
    }
    setMehr([]);
    setMehrCursor(undefined);
    setParams(n, { replace: true });
  };

  const aktion = async (l: { id: string }, a: LaufAktion) => {
    try {
      await workflowsApi.laufAktion(l.id, a);
      await g.neuLaden();
    } catch (e) {
      meldung.fehler(e, "Lauf nicht geändert");
    }
  };

  const jetztBearbeiten = async () => {
    setBearbeitet(true);
    try {
      const r = await workflowsApi.verarbeiten();
      meldung.ok(r.verarbeitet ? `${r.verarbeitet} fällige ${r.verarbeitet === 1 ? "Lauf" : "Läufe"} bearbeitet.` : "Nichts fällig.");
      await g.neuLaden();
    } catch (e) {
      meldung.fehler(e, "Nicht bearbeitet");
    } finally {
      setBearbeitet(false);
    }
  };

  const weitere = async () => {
    const cursor = mehrCursor === undefined ? g.daten?.cursor : mehrCursor;
    if (!cursor) return;
    try {
      const r = await workflowsApi.laeufe({ workflow_id: workflowId, lead_id: leadId, status, limit: 50, cursor });
      setMehr((m) => [...m, ...r.data]);
      setMehrCursor(r.has_more ? r.cursor : null);
    } catch (e) {
      meldung.fehler(e, "Nicht geladen");
    }
  };

  const ersteIds = new Set((g.daten?.data ?? []).map((l) => l.id));
  const laeufe = [...(g.daten?.data ?? []), ...mehr.filter((l) => !ersteIds.has(l.id))];
  const hatMehr = mehrCursor === undefined ? !!g.daten?.has_more : !!mehrCursor;
  const alleWorkflows = [...(workflows.daten?.data ?? []), ...(archivierte.daten?.data ?? [])];

  return (
    <div className="liste-seite wf-liste">
      <div className="liste-kopf">
        <Link to="/workflows" className="wf-kopf__zurueck" title="Zu allen Workflows">
          <Icon name="chevron-links" groesse={14} />
          Workflows
        </Link>
        <h1 className="liste-kopf__titel">Läufe</h1>
        <div className="liste-kopf__rechts">
          {kannVerwalten ? (
            <button type="button" className="knopf" disabled={bearbeitet} onClick={() => void jetztBearbeiten()} title="Fällige Läufe sofort bearbeiten (sonst alle 10 Sekunden automatisch)">
              <Icon name="neu-laden" groesse={14} />
              Jetzt ausführen
            </button>
          ) : null}
        </div>
      </div>
      <div className="liste-leiste wf-liste__leiste">
        <span className="liste-leiste__zahl">{g.daten ? `${g.daten.total ?? laeufe.length} ${g.daten.total === 1 ? "Lauf" : "Läufe"}` : ""}</span>
        <Auswahl
          optionen={alleWorkflows.map((w) => ({ wert: w.id, label: w.name, unter: w.status === "aktiv" ? null : w.status }))}
          wert={workflowId ?? null}
          leerErlaubt
          platzhalter="Alle Workflows"
          knopfKlasse="knopf knopf--klein"
          breite={280}
          onWahl={(w) => setFilter({ workflow_id: (w as string | null) ?? null })}
        />
        <Auswahl
          optionen={STATUS.map((s) => ({ wert: s, label: LAUF_STATUS[s] }))}
          wert={status ?? null}
          leerErlaubt
          platzhalter="Alle Status"
          knopfKlasse="knopf knopf--klein"
          breite={200}
          onWahl={(w) => setFilter({ status: (w as string | null) ?? null })}
        />
        {leadId ? (
          <button type="button" className="filterchip filterchip--auswahl" onClick={() => setFilter({ lead_id: null })} title="Filter entfernen">
            Ein Lead
            <Icon name="schliessen" groesse={11} />
          </button>
        ) : null}
        <span className="liste-leiste__rechts leise">Aktualisiert sich selbst</span>
      </div>
      {g.fehler && !g.daten ? <FehlerBox fehler={g.fehler} onNochmal={() => void g.neuLaden()} /> : null}
      {g.laedt && !g.daten ? (
        <Laden />
      ) : !laeufe.length ? (
        <div className="leer">
          <div className="leer__titel">Keine Läufe</div>
          <div className="leer__text">{workflowId || leadId || status ? "Mit diesem Filter gibt es keine Läufe." : "Sobald ein Workflow für einen Lead startet, steht der Lauf hier."}</div>
        </div>
      ) : (
        <>
          <LaeufeTabelle laeufe={laeufe} kannVerwalten={kannVerwalten} onAktion={aktion} />
          {hatMehr ? (
            <div className="wf-mehr">
              <button type="button" className="knopf" onClick={() => void weitere()}>
                Weitere laden
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
